import type { UseQueryResult } from '@tanstack/react-query';
import type { VonqDirectApplyQuestionnairePreview } from 'api-clients/monolith';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import ExclamationIcon from '@/assets/icons/exclamation.svg';
import ExclamationCircleIcon from '@/assets/icons/exclamation-circle.svg';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Banner } from '@/components/ui/banner';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';

import { messages, reasonMessages, typeMessages } from './messages';

/** Hire adds these to every questionnaire; alone, the opening has no questions. */
const RESERVED_QUESTION_IDS = new Set(['fountain_phone_number', 'sms_opt_in']);

interface ScreeningQuestionsProps {
  preview: Pick<
    UseQueryResult<VonqDirectApplyQuestionnairePreview>,
    'data' | 'isPending' | 'isError'
  >;
  boardName: string;
  editUrl: string;
  /** An error sits at the questionnaire facet's form path. */
  hasFacetError: boolean;
}

export function ScreeningQuestions({
  preview,
  boardName,
  editUrl,
  hasFacetError,
}: ScreeningQuestionsProps) {
  const intl = useIntl();
  const { data, isPending, isError } = preview;
  const board = { board: boardName };

  // A preview without `facet` means Direct Apply is off for this account.
  if (!isPending && !isError && !data?.facet) return null;

  const editLink = (label: MessageDescriptor) => (
    <a
      href={editUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-body text-fx-primary focus-visible:outline-fx-primary inline-flex items-center gap-0.5 font-medium hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {intl.formatMessage(label)}
      <span aria-hidden="true">
        <ExternalLinkIcon className="text-fx-primary" />
      </span>
      <span className="sr-only"> {intl.formatMessage(messages.newTab)}</span>
    </a>
  );

  // Source UI's Banner sets no live-region role, so each banner gets one here.
  const errorBanner = (description: React.ReactNode) => (
    <div role="alert" className="mt-2">
      <Banner
        color="rose"
        icon={<img src={ExclamationIcon} alt="" className="self-start" />}
        description={description}
      />
    </div>
  );

  const helper = (text: string) => (
    <Typography variant="small1" className="text-fx-neutral-500">
      {text}
    </Typography>
  );

  const body = () => {
    if (isPending) {
      return (
        <>
          {helper(intl.formatMessage(messages.helper, board))}
          <div
            role="status"
            aria-busy="true"
            aria-label={intl.formatMessage(messages.loading)}
            className="border-fx-neutral-200 mt-1 flex flex-col gap-3 rounded-sm border p-3"
          >
            <Skeleton aria-hidden="true" className="h-4 w-full" />
            <Skeleton aria-hidden="true" className="h-4 w-full" />
            <Skeleton aria-hidden="true" className="h-4 w-full" />
          </div>
        </>
      );
    }

    if (isError || !data) {
      return errorBanner(intl.formatMessage(messages.loadError));
    }

    if (!data.valid || hasFacetError) {
      return errorBanner(
        <div className="flex flex-col items-start gap-1">
          <Typography variant="body1">
            {intl.formatMessage(messages.rejected, board)}
          </Typography>
          {editLink(messages.editQuestions)}
        </div>,
      );
    }

    const hasOwnQuestions = data.questions.some(
      question => !RESERVED_QUESTION_IDS.has(question.id),
    );
    if (!hasOwnQuestions && data.skipped.length === 0) {
      return (
        <>
          {helper(intl.formatMessage(messages.noQuestions))}
          <div>{editLink(messages.addQuestions)}</div>
        </>
      );
    }

    return (
      <>
        {helper(intl.formatMessage(messages.helper, board))}
        <ul
          aria-label={intl.formatMessage(messages.questionsSent)}
          className="border-fx-neutral-200 divide-fx-neutral-200 mt-1 flex flex-col divide-y rounded-sm border"
        >
          {data.questions.map(question => (
            <li
              key={question.id}
              className="flex items-center justify-between gap-4 px-3 py-2"
            >
              <Typography variant="body1" className="min-w-0 truncate">
                {question.label}
              </Typography>
              {typeMessages[question.type] && (
                <Tag
                  className="bg-fx-neutral-100 shrink-0"
                  text={intl.formatMessage(typeMessages[question.type])}
                />
              )}
            </li>
          ))}
        </ul>
        {data.skipped.length > 0 && (
          <div role="status" className="mt-2">
            <Banner
              color="amber"
              icon={
                <img
                  src={ExclamationCircleIcon}
                  alt=""
                  className="self-start"
                />
              }
              title={intl.formatMessage(messages.skippedTitle, {
                ...board,
                count: data.skipped.length,
              })}
              description={
                <div className="flex flex-col items-start gap-1">
                  <ul className="text-body text-fx-neutral-900 ml-5 list-disc">
                    {data.skipped.map(item => (
                      <li key={`${item.label}-${item.reason}`}>
                        {reasonMessages[item.reason]
                          ? intl.formatMessage(messages.skippedLine, {
                              question: item.label,
                              reason: intl.formatMessage(
                                reasonMessages[item.reason],
                                { ...board, limit: item.limit },
                              ),
                            })
                          : item.label}
                      </li>
                    ))}
                  </ul>
                  {editLink(messages.editQuestions)}
                </div>
              }
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <Typography variant="subtitle2">
        {intl.formatMessage(messages.label)}
      </Typography>
      {body()}
    </div>
  );
}
